import type { Answers, Rule, RuleRef, SurveyContext, Template, Item, Section } from './schema.js';

export interface RuleScope {
  answers: Answers;
  context: SurveyContext;
}

function readRef(ref: RuleRef, scope: RuleScope): unknown {
  if ('field' in ref) return scope.answers.fields[ref.field];
  if ('control' in ref) return scope.answers.controls[ref.control]?.value ?? null;
  const key = ref.context.split('.')[1] as keyof SurveyContext['site'];
  return scope.context.site[key] ?? null;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Equality used by rules. Number, rating and calculated answers are numbers;
 * a value typed into a rule (or an older template) may be the string "3".
 * When either side is a number both are compared numerically, so 3 equals
 * "3" — but two strings are compared as written ("001" is not "1").
 */
export function ruleValueEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'number' && typeof b !== 'number') return false;
  const na = asNumber(a);
  const nb = asNumber(b);
  return na !== null && nb !== null && na === nb;
}

const inList = (list: unknown, v: unknown) => Array.isArray(list) && list.some((x) => ruleValueEquals(v, x));

export function evaluateRule(rule: Rule, scope: RuleScope): boolean {
  if ('all' in rule) return rule.all.every((r) => evaluateRule(r, scope));
  if ('any' in rule) return rule.any.some((r) => evaluateRule(r, scope));
  if ('not' in rule) return !evaluateRule(rule.not, scope);

  const v = readRef(rule.ref, scope);
  switch (rule.op) {
    case 'eq':
      return !isEmpty(v) && ruleValueEquals(v, rule.value);
    case 'neq':
      return isEmpty(v) || !ruleValueEquals(v, rule.value);
    case 'in':
      return !isEmpty(v) && inList(rule.value, v);
    case 'notIn':
      return !inList(rule.value, v);
    case 'includes':
      return inList(v, rule.value);
    case 'includesAny':
      return Array.isArray(v) && Array.isArray(rule.value) && rule.value.some((x) => inList(v, x));
    case 'answered':
      return !isEmpty(v);
    case 'notAnswered':
      return isEmpty(v);
  }
}

export function isSectionVisible(section: Section, scope: RuleScope): boolean {
  return !section.visibleWhen || evaluateRule(section.visibleWhen, scope);
}

export function isItemVisible(section: Section, item: Item, scope: RuleScope): boolean {
  if (!isSectionVisible(section, scope)) return false;
  return !item.visibleWhen || evaluateRule(item.visibleWhen, scope);
}

/** Collect every reference used by rules, so templates can be checked for dangling refs. */
export function collectRuleRefs(rule: Rule | undefined, out: RuleRef[] = []): RuleRef[] {
  if (!rule) return out;
  if ('all' in rule) rule.all.forEach((r) => collectRuleRefs(r, out));
  else if ('any' in rule) rule.any.forEach((r) => collectRuleRefs(r, out));
  else if ('not' in rule) collectRuleRefs(rule.not, out);
  else out.push(rule.ref);
  return out;
}

/** Problems with rule references in a template: unknown keys/codes, or self/forward cycles. */
export function checkRuleReferences(template: Template): string[] {
  const keys = new Set<string>();
  const codes = new Set<string>();
  for (const s of template.sections) {
    for (const it of s.items) {
      if (it.kind === 'field') keys.add(it.key);
      if (it.kind === 'control') codes.add(it.code);
    }
  }
  const problems: string[] = [];
  const check = (where: string, rule: Rule | undefined, self?: string) => {
    for (const ref of collectRuleRefs(rule)) {
      if ('field' in ref) {
        if (!keys.has(ref.field)) problems.push(`${where}: unknown field "${ref.field}"`);
        if (self && ref.field === self) problems.push(`${where}: rule refers to itself`);
      }
      if ('control' in ref) {
        if (!codes.has(ref.control)) problems.push(`${where}: unknown control "${ref.control}"`);
        if (self && ref.control === self) problems.push(`${where}: rule refers to itself`);
      }
    }
  };
  for (const s of template.sections) {
    check(`section ${s.code}`, s.visibleWhen);
    for (const it of s.items) {
      const self = it.kind === 'field' ? it.key : it.kind === 'control' ? it.code : undefined;
      check(`item ${it.id}`, it.visibleWhen, self);
    }
  }
  return problems;
}

/** The field key or control code an item stores its answer under (layout items have none). */
export function itemRef(item: Item): string | null {
  return item.kind === 'field' ? item.key : item.kind === 'control' ? item.code : null;
}

function refName(ref: RuleRef): string | null {
  return 'field' in ref ? ref.field : 'control' in ref ? ref.control : null;
}

/**
 * What a reporter may change on a RETURNED survey (ADR-0012): the items the
 * analyst flagged, plus every item whose visibility depends on one of them
 * (directly, through its section, or through another such item). Changing a
 * flagged answer can reveal a question that was hidden at submission; that
 * question must be answerable, and the server must keep its answer.
 */
export function returnedEditableRefs(template: Template, flagged: Iterable<string>): Set<string> {
  const editable = new Set(flagged);
  const depends = (rule: Rule | undefined) => collectRuleRefs(rule).some((r) => {
    const n = refName(r);
    return n !== null && editable.has(n);
  });
  for (let changed = true; changed; ) {
    changed = false;
    for (const s of template.sections) {
      const sectionDepends = depends(s.visibleWhen);
      for (const it of s.items) {
        const ref = itemRef(it);
        if (!ref || editable.has(ref)) continue;
        if (sectionDepends || depends(it.visibleWhen)) {
          editable.add(ref);
          changed = true;
        }
      }
    }
  }
  return editable;
}
